import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Search as SearchIcon,
  Gavel as GavelIcon,
  Assignment as AssignmentIcon,
  GetApp as DownloadIcon,
  Error as ErrorIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

interface TitleDefect {
  category: string;
  defect: string;
  description: string;
  commonCauses: string[];
  resolutionMethods: string[];
  timeframe: string;
  complexity: 'Low' | 'Medium' | 'High';
  cost: string;
  preventionTips: string[];
}

const titleDefects: TitleDefect[] = [
  {
    category: 'Ownership Issues',
    defect: 'Missing Heirs',
    description: 'Property passed through estate but not all heirs were identified or located',
    commonCauses: [
      'Incomplete probate proceedings',
      'Unknown or estranged family members',
      'Adoption records not properly updated',
      'Out-of-state or foreign heirs'
    ],
    resolutionMethods: [
      'Conduct thorough heir search',
      'Obtain affidavits from known heirs',
      'File quiet title action if necessary',
      'Purchase heir finder services',
      'Obtain title insurance with specific coverage'
    ],
    timeframe: '2-6 months',
    complexity: 'High',
    cost: '$1,500 - $15,000+',
    preventionTips: [
      'Thorough probate review during search',
      'Cross-reference obituaries and death records',
      'Check adoption and name change records'
    ]
  },
  {
    category: 'Ownership Issues',
    defect: 'Forged Documents',
    description: 'Deed or other conveyance document contains forged signatures',
    commonCauses: [
      'Identity theft',
      'Fraudulent power of attorney',
      'Signature by unauthorized party',
      'Document alteration after execution'
    ],
    resolutionMethods: [
      'Handwriting analysis by expert',
      'Criminal investigation if warranted',
      'Quiet title action',
      'Title insurance claim',
      'Obtain new deed from rightful owner'
    ],
    timeframe: '3-12 months',
    complexity: 'High',
    cost: '$5,000 - $25,000+',
    preventionTips: [
      'Verify identity of all parties',
      'Use notary acknowledgments properly',
      'Compare signatures on multiple documents'
    ]
  },
  {
    category: 'Liens and Encumbrances',
    defect: 'Unreleased Mortgage',
    description: 'Mortgage was paid off but satisfaction was never recorded',
    commonCauses: [
      'Lender oversight or error',
      'Bank merger or acquisition',
      'Lost satisfaction document',
      'Recording error at county level'
    ],
    resolutionMethods: [
      'Contact original lender for satisfaction',
      'Obtain affidavit of payment from borrower',
      'File suit to compel satisfaction',
      'Use statutory discharge procedures',
      'Bond around the lien'
    ],
    timeframe: '2-8 weeks',
    complexity: 'Medium',
    cost: '$500 - $3,000',
    preventionTips: [
      'Follow up on satisfaction recording',
      'Maintain contact with lenders',
      'Check recordings 30 days after payoff'
    ]
  },
  {
    category: 'Liens and Encumbrances',
    defect: 'Tax Liens',
    description: 'Federal, state, or local tax liens against the property or owner',
    commonCauses: [
      'Unpaid income taxes',
      'Property tax delinquency',
      'Business tax obligations',
      'Estate tax issues'
    ],
    resolutionMethods: [
      'Pay lien in full',
      'Negotiate payment plan with taxing authority',
      'Apply for lien withdrawal',
      'Wait for statute of limitations',
      'Contest lien validity if appropriate'
    ],
    timeframe: '1-12 weeks',
    complexity: 'Medium',
    cost: 'Lien amount + penalties + interest',
    preventionTips: [
      'Regular tax lien searches',
      'Check federal and state databases',
      'Review personal property tax records'
    ]
  },
  {
    category: 'Survey Issues',
    defect: 'Boundary Disputes',
    description: 'Disagreement about property boundaries with adjacent owners',
    commonCauses: [
      'Inaccurate or conflicting surveys',
      'Monuments destroyed or moved',
      'Overlapping descriptions',
      'Prescriptive rights claims'
    ],
    resolutionMethods: [
      'Obtain new professional survey',
      'Negotiate boundary line agreement',
      'File quiet title or partition action',
      'Mediation or arbitration',
      'Title insurance coverage'
    ],
    timeframe: '1-6 months',
    complexity: 'Medium',
    cost: '$2,000 - $15,000+',
    preventionTips: [
      'Require current survey for all transactions',
      'Compare surveys from different years',
      'Check for recorded boundary agreements'
    ]
  },
  {
    category: 'Survey Issues',
    defect: 'Encroachments',
    description: 'Structures or improvements crossing property boundaries',
    commonCauses: [
      'Building without proper survey',
      'Fence installation errors',
      'Driveway or utility placement',
      'Natural boundary changes'
    ],
    resolutionMethods: [
      'Negotiate encroachment agreement',
      'Remove encroaching improvement',
      'Purchase additional land',
      'Quiet title action',
      'Title insurance endorsement'
    ],
    timeframe: '2-8 weeks',
    complexity: 'Low',
    cost: '$500 - $5,000',
    preventionTips: [
      'Current survey before construction',
      'Mark boundaries clearly',
      'Review neighbor improvements'
    ]
  },
  {
    category: 'Legal Issues',
    defect: 'Judgment Liens',
    description: 'Court judgments creating liens against the property',
    commonCauses: [
      'Civil lawsuit judgments',
      'Contractor or supplier claims',
      'Homeowner association liens',
      'Unpaid child support'
    ],
    resolutionMethods: [
      'Pay judgment in full',
      'Negotiate settlement',
      'Appeal judgment if grounds exist',
      'Wait for judgment to expire',
      'Bond around the judgment'
    ],
    timeframe: '2-12 weeks',
    complexity: 'Medium',
    cost: 'Judgment amount + interest + costs',
    preventionTips: [
      'Regular judgment searches',
      'Check federal and state courts',
      'Review litigation history'
    ]
  },
  {
    category: 'Legal Issues',
    defect: 'Easement Disputes',
    description: 'Conflicts over easement rights and usage',
    commonCauses: [
      'Vague easement language',
      'Changed property use',
      'Abandonment claims',
      'Scope of use disagreements'
    ],
    resolutionMethods: [
      'Clarify easement terms through agreement',
      'Quiet title action',
      'Easement modification or termination',
      'Mediation between parties',
      'Title insurance coverage'
    ],
    timeframe: '1-4 months',
    complexity: 'Medium',
    cost: '$1,000 - $10,000',
    preventionTips: [
      'Review all recorded easements',
      'Inspect property for easement usage',
      'Clarify easement scope in agreements'
    ]
  },
  {
    category: 'Recording Issues',
    defect: 'Chain of Title Gaps',
    description: 'Missing links in the ownership chain',
    commonCauses: [
      'Unrecorded deeds',
      'Lost or destroyed documents',
      'Recording errors',
      'Name variations not connected'
    ],
    resolutionMethods: [
      'Locate missing documents',
      'Obtain corrective deeds',
      'File suit to establish title',
      'Use statutory procedures',
      'Title insurance coverage'
    ],
    timeframe: '1-6 months',
    complexity: 'High',
    cost: '$2,000 - $10,000',
    preventionTips: [
      'Thorough title search',
      'Cross-reference name variations',
      'Check multiple recording systems'
    ]
  },
  {
    category: 'Recording Issues',
    defect: 'Incorrect Legal Description',
    description: 'Property description errors in recorded documents',
    commonCauses: [
      'Transcription errors',
      'Survey mistakes',
      'Metes and bounds errors',
      'Reference to wrong plat'
    ],
    resolutionMethods: [
      'Record corrective deed',
      'Obtain court order for correction',
      'Use scriveners affidavit',
      'Re-survey property',
      'Quiet title action if necessary'
    ],
    timeframe: '2-8 weeks',
    complexity: 'Low',
    cost: '$500 - $3,000',
    preventionTips: [
      'Verify legal descriptions carefully',
      'Compare with survey and prior deeds',
      'Use professional surveyor input'
    ]
  }
];

const defectCategories = [
  'All Categories',
  'Ownership Issues',
  'Liens and Encumbrances', 
  'Survey Issues',
  'Legal Issues',
  'Recording Issues'
];

const TitleDefectsGuide: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [expandedDefect, setExpandedDefect] = useState<string | false>(false);

  const filteredDefects = titleDefects.filter(defect => {
    const matchesSearch = defect.defect.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         defect.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         defect.commonCauses.some(cause => cause.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'All Categories' || defect.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const handleDefectChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedDefect(isExpanded ? panel : false);
  };

  const getComplexityColor = (complexity: string) => {
    switch (complexity) {
      case 'Low': return '#16a34a';
      case 'Medium': return '#f59e0b';
      case 'High': return '#dc2626';
      default: return '#64748b';
    }
  };

  return (
    <>
      <Helmet>
        <title>Title Defects & Problems Guide | Professional Reference | MAD Title Agency</title>
        <meta
          name="description"
          content="Comprehensive guide to common title defects and resolution methods. Professional reference for title issues, liens, survey problems, and curative procedures in New Jersey."
        />
        <meta
          name="keywords"
          content="title defects, title problems, title curative, title issues, New Jersey title defects, title insurance claims, quiet title action"
        />
      </Helmet>

      {/* Breadcrumb */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 2 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link> →{' '}
            <Link to="/resources" style={{ textDecoration: 'none', color: 'inherit' }}>Resources</Link> →{' '}
            Title Defects Guide
          </Typography>
        </Container>
      </Box>

      {/* Header */}
      <Box sx={{ backgroundColor: '#dc2626', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Chip
                label="Professional Reference"
                sx={{
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  mb: 2,
                }}
              />
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                }}
              >
                Title Defects & Problems Guide
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
                Comprehensive reference for common title issues and proven resolution methods
              </Typography>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  backgroundColor: '#f59e0b',
                  '&:hover': { backgroundColor: '#d97706' },
                }}
              >
                Download Reference
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <WarningIcon sx={{ fontSize: 64, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {titleDefects.length} Common Defects
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Resolution Strategies
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Introduction */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#dc2626' }}>
            Professional Title Defect Reference
          </Typography>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
                Title defects can significantly impact real estate transactions and property ownership. 
                This comprehensive guide provides title professionals with detailed information about 
                common defects, their causes, and proven resolution methods.
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
                Each defect entry includes typical timeframes, cost estimates, and prevention strategies 
                to help you efficiently resolve issues and advise clients on potential solutions.
              </Typography>
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Important:</strong> This guide provides general information only. Always consult 
                  with qualified legal counsel for specific defect resolution strategies and comply with 
                  all applicable New Jersey laws and regulations.
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, backgroundColor: '#fef3c7' }}>
                <ErrorIcon sx={{ fontSize: 48, color: '#dc2626', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Quick Statistics
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="15% of transactions have title issues" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Most defects curable within 30-90 days" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Proactive searching reduces problems" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Title insurance protects against unknown defects" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Search and Filter */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ mb: 3, fontWeight: 600, color: '#dc2626' }}>
            Search Title Defects
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search defects, causes, or solutions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {defectCategories.map((category) => (
                  <Chip
                    key={category}
                    label={category}
                    onClick={() => setSelectedCategory(category)}
                    color={selectedCategory === category ? 'primary' : 'default'}
                    variant={selectedCategory === category ? 'filled' : 'outlined'}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: selectedCategory === category ? 'primary.dark' : 'grey.100' },
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Defects List */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#dc2626' }}>
            Common Title Defects ({filteredDefects.length} found)
          </Typography>

          {filteredDefects.map((defect, index) => (
            <Accordion
              key={defect.defect}
              expanded={expandedDefect === defect.defect}
              onChange={handleDefectChange(defect.defect)}
              sx={{ mb: 2, border: '1px solid #e2e8f0' }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {defect.defect}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {defect.description}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
                    <Chip
                      label={defect.category}
                      size="small"
                      sx={{ backgroundColor: '#e2e8f0', color: '#374151' }}
                    />
                    <Chip
                      label={defect.complexity}
                      size="small"
                      sx={{
                        backgroundColor: getComplexityColor(defect.complexity),
                        color: 'white',
                      }}
                    />
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#dc2626' }}>
                      Common Causes
                    </Typography>
                    <List>
                      {defect.commonCauses.map((cause, idx) => (
                        <ListItem key={idx}>
                          <ListItemIcon>
                            <ErrorIcon sx={{ color: '#dc2626' }} />
                          </ListItemIcon>
                          <ListItemText primary={cause} />
                        </ListItem>
                      ))}
                    </List>

                    <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 600, color: '#16a34a' }}>
                      Prevention Tips
                    </Typography>
                    <List>
                      {defect.preventionTips.map((tip, idx) => (
                        <ListItem key={idx}>
                          <ListItemIcon>
                            <CheckIcon sx={{ color: '#16a34a' }} />
                          </ListItemIcon>
                          <ListItemText primary={tip} />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#059669' }}>
                      Resolution Methods
                    </Typography>
                    <List>
                      {defect.resolutionMethods.map((method, idx) => (
                        <ListItem key={idx}>
                          <ListItemIcon>
                            <BuildIcon sx={{ color: '#059669' }} />
                          </ListItemIcon>
                          <ListItemText primary={method} />
                        </ListItem>
                      ))}
                    </List>

                    <Box sx={{ mt: 3, p: 2, backgroundColor: '#f8fafc', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Resolution Details:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Timeframe:</strong> {defect.timeframe}<br />
                        <strong>Estimated Cost:</strong> {defect.cost}<br />
                        <strong>Complexity:</strong> {defect.complexity}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Quick Reference Table */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#dc2626' }}>
            Quick Reference Table
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell><strong>Defect Type</strong></TableCell>
                  <TableCell><strong>Category</strong></TableCell>
                  <TableCell><strong>Complexity</strong></TableCell>
                  <TableCell><strong>Typical Timeframe</strong></TableCell>
                  <TableCell><strong>Cost Range</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDefects.map((defect, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {defect.defect}
                      </Typography>
                    </TableCell>
                    <TableCell>{defect.category}</TableCell>
                    <TableCell>
                      <Chip
                        label={defect.complexity}
                        size="small"
                        sx={{
                          backgroundColor: getComplexityColor(defect.complexity),
                          color: 'white',
                        }}
                      />
                    </TableCell>
                    <TableCell>{defect.timeframe}</TableCell>
                    <TableCell>{defect.cost}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Best Practices */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#dc2626' }}>
            Best Practices for Title Defect Management
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%', backgroundColor: '#f0fdfa' }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Prevention Strategies
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Thorough Title Search"
                      secondary="Extend search period for complex properties"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Current Survey Required"
                      secondary="Insist on recent surveys for all transactions"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Verify All Parties"
                      secondary="Confirm identity and authority of all signers"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Regular Database Updates"
                      secondary="Check multiple lien and judgment databases"
                    />
                  </ListItem>
                </List>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%', backgroundColor: '#fef3c7' }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Resolution Best Practices
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <BuildIcon sx={{ color: '#f59e0b' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Act Quickly"
                      secondary="Address defects as soon as discovered"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <BuildIcon sx={{ color: '#f59e0b' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Document Everything"
                      secondary="Maintain detailed records of all curative efforts"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <BuildIcon sx={{ color: '#f59e0b' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Communicate Clearly"
                      secondary="Keep all parties informed of progress and options"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <BuildIcon sx={{ color: '#f59e0b' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Know When to Refer"
                      secondary="Engage qualified counsel for complex issues"
                    />
                  </ListItem>
                </List>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Related Resources */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#dc2626' }}>
            Related Professional Resources
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <AssignmentIcon sx={{ fontSize: 48, color: '#dc2626', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Title Search Manual
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Comprehensive procedures for effective title examination.
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/resources/title-search-manual"
                  fullWidth
                >
                  View Manual
                </Button>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <CheckIcon sx={{ fontSize: 48, color: '#dc2626', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  ALTA Compliance
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Best practices checklist and compliance tracking.
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/resources/alta-compliance-checklist"
                  fullWidth
                >
                  View Checklist
                </Button>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <GavelIcon sx={{ fontSize: 48, color: '#dc2626', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Recording Requirements
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  County-specific recording procedures and requirements.
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/resources/nj-recording-requirements"
                  fullWidth
                >
                  View Requirements
                </Button>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: '#fef2f2', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 600, color: '#64748b' }}>
              Facing Complex Title Issues?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
              Our experienced title professionals can help resolve even the most challenging defects.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Consult Our Experts
              </Button>
              <Button
                variant="outlined"
                size="large"
                href="mailto:madtitleagency@gmail.com"
                sx={{
                  borderColor: '#dc2626',
                  color: '#dc2626',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Curative Services
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default TitleDefectsGuide; 